import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db as firestore } from '../../config/firebase';
import { collection, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { OffcanvasMenu, Header } from '../../components/common';
import { MobileFilterSheet, MobileFilterOptions } from '../../components/common/MobileFilterSheet';
import { MobileFilterChips } from '../../components/common/MobileFilterChips';
import styled from 'styled-components';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows, Transitions } from '../../styles/ThemeStyles';
import { getAssignedStores } from '../../utils/storeAssignment';
import type { Store as StoreType } from '../../@types/store';
import { Filter, Home, Store, RefreshCw } from 'lucide-react';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${ThemeColors.background.default};
`;

const MainContent = styled.main`
  padding: ${Spacing.md} ${Spacing.md} ${Spacing.xxl};
  padding-top: calc(${Spacing.lg} + 64px); /* Account for fixed header */
`;

const PageTitle = styled.h1`
  font-size: ${Typography.fontSize.xxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.md} 0;
  padding-bottom: ${Spacing.sm};
  border-bottom: 2px solid ${ThemeColors.primary.light};
`;

const TabContainer = styled.div`
  display: flex;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.lg};
  border-bottom: 2px solid ${ThemeColors.border.light};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm} ${Spacing.md};
  background-color: ${props => props.active ? ThemeColors.background.paper : 'transparent'};
  border: none;
  border-bottom: 3px solid ${props => props.active ? ThemeColors.primary.main : 'transparent'};
  cursor: pointer;
  font-size: ${Typography.fontSize.md};
  font-weight: ${props => props.active ? Typography.fontWeight.bold : Typography.fontWeight.medium};
  color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.text.secondary};
  transition: all ${Transitions.duration.short} ease;
  position: relative;
  margin-bottom: -2px;
  white-space: nowrap;

  &:active {
    background-color: ${ThemeColors.background.default};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TabBadge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 ${Spacing.xs};
  background-color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.gray[300]};
  color: ${props => props.active ? '#fff' : ThemeColors.text.secondary};
  border-radius: 11px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: ${Spacing.xs};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.md};
`;

const RefreshButton = styled.button<{ spinning?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.md};
  background-color: ${ThemeColors.background.paper};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.md};
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  svg {
    animation: ${props => props.spinning ? 'spin 0.8s linear infinite' : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  &:active {
    background-color: ${ThemeColors.background.default};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchSection = styled.div`
  margin-bottom: ${Spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm};
`;

const SearchContainer = styled.div`
  position: relative;
  margin-top: ${Spacing.md};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.md} ${Spacing.lg};
  padding-left: 48px;
  border-radius: ${BorderRadius.lg};
  border: 1px solid ${ThemeColors.border.light};
  font-size: ${Typography.fontSize.md};
  background-color: ${ThemeColors.background.paper};
  box-shadow: ${Shadows.sm};
  transition: all ${Transitions.duration.short} ease;
  
  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: ${Shadows.md};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${ThemeColors.text.secondary};
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const StoreCard = styled.div`
  background-color: ${ThemeColors.background.paper};
  border-radius: ${BorderRadius.md};
  padding: ${Spacing.md};
  box-shadow: ${Shadows.sm};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ease;
  border-left: 3px solid ${ThemeColors.primary.main};
  
  &:active {
    transform: translateY(1px);
    box-shadow: ${Shadows.xs};
  }
`;

const StoreName = styled.h2`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.xs} 0;
  line-height: 1.3;
`;

const CustomerNumberBadge = styled.div`
  display: inline-block;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.primary.main};
  font-weight: ${Typography.fontWeight.semiBold};
  margin-bottom: ${Spacing.sm};
  background-color: ${ThemeColors.primary.light}20;
  padding: 4px ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
`;

const InfoSection = styled.div`
  margin: ${Spacing.sm} 0;
`;

const StoreAddress = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0 0 ${Spacing.xs} 0;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  
  svg {
    min-width: 18px;
    margin-right: ${Spacing.xs};
    margin-top: 2px;
    color: ${ThemeColors.text.hint};
  }
`;

const StorePhone = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin: 0;
  font-weight: ${Typography.fontWeight.medium};
  display: flex;
  align-items: center;
  
  svg {
    min-width: 18px;
    margin-right: ${Spacing.xs};
    color: ${ThemeColors.text.hint};
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${ThemeColors.border.light};
  margin: ${Spacing.sm} 0;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${Spacing.sm};
  margin-top: ${Spacing.sm};
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'tertiary' }>`
  padding: ${Spacing.xs} ${Spacing.sm};
  border: none;
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  transition: all ${Transitions.duration.short} ease;
  
  background-color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.main :
    props.variant === 'secondary' ? ThemeColors.secondary.main : 
    ThemeColors.background.paper
  };
  
  color: ${props => 
    props.variant === 'primary' ? ThemeColors.primary.contrastText :
    props.variant === 'secondary' ? ThemeColors.secondary.contrastText : 
    ThemeColors.primary.main
  };
  
  border: ${props => 
    props.variant === 'tertiary' ? `1px solid ${ThemeColors.primary.main}` : 'none'
  };
  
  &:active {
    transform: scale(0.96);
    opacity: 0.9;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ButtonText = styled.span`
  font-size: ${Typography.fontSize.xs};
  margin-top: 2px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 300px;
  gap: ${Spacing.md};
`;

const LoadingSpinner = styled.div`
  border: 3px solid ${ThemeColors.gray[200]};
  border-top: 3px solid ${ThemeColors.primary.main};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${Typography.fontSize.md};
  color: ${ThemeColors.text.secondary};
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xl} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.md};
`;

const EmptyStateIcon = styled.div`
  color: ${ThemeColors.gray[300]};
  margin-bottom: ${Spacing.md};
  
  svg {
    width: 64px;
    height: 64px;
  }
`;

const EmptyStateText = styled.h3`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  margin: 0;
  color: ${ThemeColors.text.primary};
`;

const EmptyStateSubtext = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const FilterButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.md} ${Spacing.lg};
  background-color: ${props => props.active ? ThemeColors.primary.main : ThemeColors.background.paper};
  color: ${props => props.active ? ThemeColors.primary.contrastText : ThemeColors.text.primary};
  border: 1px solid ${props => props.active ? ThemeColors.primary.main : ThemeColors.border.light};
  border-radius: ${BorderRadius.md};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ease;
  margin-top: ${Spacing.md};
  width: 100%;
  box-shadow: ${Shadows.sm};

  &:active {
    opacity: 0.8;
    transform: scale(0.98);
  }

  &:hover {
    box-shadow: ${Shadows.md};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: ${ThemeColors.error.main};
  color: white;
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  margin-left: ${Spacing.xs};
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin-bottom: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  flex-wrap: wrap;
  padding-top: ${Spacing.sm};
  
  @media (max-width: 480px) {
    font-size: ${Typography.fontSize.xs};
    gap: 4px;
  }
`;

const BreadcrumbItem = styled.span<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.clickable ? ThemeColors.primary.main : ThemeColors.text.secondary};
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.clickable ? ThemeColors.primary.dark : ThemeColors.text.secondary};
  }
  
  svg {
    width: 14px;
    height: 14px;
    
    @media (max-width: 480px) {
      width: 12px;
      height: 12px;
    }
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${ThemeColors.border.medium};
  margin: 0 2px;
  
  @media (max-width: 480px) {
    margin: 0 1px;
  }
`;

interface Store {
  id: string;
  CustomerNo: string;
  StoreName: string;
  Address: string;
  City: string;
  Province: string;
  PostalCode: string;
  Phone: string;
  Email: string;
  StoreType: string;
}

type TabType = 'assigned' | 'all';

export const StoreVisitScreenMobile = () => {
  const { t } = useTranslation();
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(true);
  const [stores, setStores] = useState<Store[]>([]);
  const [assignedStores, setAssignedStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('assigned');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<MobileFilterOptions>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const fetchStores = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const storesRef = collection(firestore, 'Stores');
      
      // Don't use orderBy - just get all stores like desktop version
      const querySnapshot = await getDocs(storesRef);
      
      console.log('Total stores found:', querySnapshot.size);
      
      const storesData: Store[] = [];
      const storeTypesData: StoreType[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Skip archived (soft-deleted) stores so they don't appear in Store Visit
        if ((data.status || data.Status) === 'archived') return;
        console.log('Store data:', { id: doc.id, ...data });
        
        // Map Firestore fields to Store interface - matching desktop logic
        const store: Store = {
          id: doc.id,
          CustomerNo: data.CustomerNo || `CUST${doc.id.slice(-3).toUpperCase()}`,
          StoreName: data.StoreName || 'Unknown Store',
          Address: data.Address || '',
          City: data.City || '',
          Province: data.Prov || '',
          PostalCode: data.PostalCode || '',
          Phone: data.Telephone || '',
          Email: data.EmailAdddress || '',
          StoreType: data.SalesGroup || ''
        };
        
        // Store raw data for comprehensive search
        (store as any)._rawData = data;
        
        storesData.push(store);
        
        // Also build StoreType array with assignedToUserIds from Firestore
        storeTypesData.push({
          id: doc.id,
          CustomerNo: store.CustomerNo,
          StoreName: store.StoreName,
          Address: store.Address,
          City: store.City,
          Prov: store.Province,
          PostalCode: store.PostalCode,
          Telephone: store.Phone,
          EmailAdddress: store.Email,
          SalespersonNo: '',
          salesGroup: store.StoreType,
          assignedToUserIds: data.assignedToUserIds || [],
          status: 'active',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      });
      
      console.log('Processed stores:', storesData);

      // Fetch user data to get assigned stores
      const userDoc = await getDoc(doc(firestore, 'users', user.id));
      const userData = userDoc.data();

      // Get assigned stores using utility function - same as desktop
      // Pass user's assignedStoreIds as fallback
      const assigned = await getAssignedStores(
        user.id, 
        storeTypesData, 
        userData?.salesGroup,
        userData?.assignedStoreIds
      );
      
      // Convert assigned stores back to Store format - matching desktop logic
      // Create a map of store IDs to raw data for quick lookup
      const storeRawDataMap = new Map<string, any>();
      storesData.forEach(store => {
        storeRawDataMap.set(store.id, (store as any)._rawData);
      });
      
      const assignedData: Store[] = assigned.map(s => {
        const assignedStore: Store = {
          id: s.id,
          CustomerNo: s.CustomerNo || '',
          StoreName: s.StoreName || 'Unknown Store',
          Address: s.Address || '',
          City: s.City || '',
          Province: s.Prov || '',
          PostalCode: s.PostalCode || '',
          Phone: s.Telephone || '',
          Email: s.EmailAdddress || '',
          StoreType: s.salesGroup || ''
        };
        
        // Preserve raw data for comprehensive search
        (assignedStore as any)._rawData = storeRawDataMap.get(s.id) || {};
        
        return assignedStore;
      });

      console.log(`Assigned stores for user ${user.id}:`, assignedData.length);
      
      setStores(storesData);
      setAssignedStores(assignedData);
      setFilteredStores(activeTab === 'assigned' ? assignedData : storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, [user]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchStores();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Extract unique values for filters
  const availableProvinces = useMemo(() => {
    const sourceStores = activeTab === 'assigned' ? assignedStores : stores;
    return Array.from(new Set(sourceStores.map(s => s.Province).filter(Boolean))).sort() as string[];
  }, [stores, assignedStores, activeTab]);

  const availableCities = useMemo(() => {
    const sourceStores = activeTab === 'assigned' ? assignedStores : stores;
    return Array.from(new Set(sourceStores.map(s => s.City).filter(Boolean))).sort() as string[];
  }, [stores, assignedStores, activeTab]);

  const availableSalesGroups = useMemo(() => {
    const sourceStores = activeTab === 'assigned' ? assignedStores : stores;
    return Array.from(new Set(sourceStores.map(s => s.StoreType).filter(Boolean))).sort() as string[];
  }, [stores, assignedStores, activeTab]);

  useEffect(() => {
    // Determine which store list to filter based on active tab
    const sourceStores = activeTab === 'assigned' ? assignedStores : stores;

    let filtered = sourceStores;

    // Apply text search filter
    // Check both mapped fields and raw database fields to catch all variations
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(store => {
        const rawData = (store as any)._rawData || {};
        
        // Helper to safely check if a value contains the query
        const matches = (value: any): boolean => {
          if (!value) return false;
          return String(value).toLowerCase().includes(query);
        };
        
        return (
          matches(store.CustomerNo) ||
          matches((store as any).customerNo) ||
          matches(rawData.CustomerNo) ||
          matches(rawData.customerNo) ||
          matches(store.StoreName) ||
          matches((store as any).name) ||
          matches((store as any).Name) ||
          matches(rawData.StoreName) ||
          matches(rawData.Name) ||
          matches(rawData.name) ||
          matches(store.City) ||
          matches((store as any).city) ||
          matches(rawData.City) ||
          matches(rawData.city) ||
          matches(store.Address) ||
          matches((store as any).address) ||
          matches(rawData.Address) ||
          matches(rawData.address) ||
          matches(store.Email) ||
          matches((store as any).email) ||
          matches(rawData.EmailAdddress) ||
          matches(rawData.EmailAddress) ||
          matches(rawData.Email) ||
          matches(rawData.email) ||
          matches(store.Phone) ||
          matches((store as any).phone) ||
          matches(store.Telephone) ||
          matches((store as any).telephone) ||
          matches(rawData.Telephone) ||
          matches(rawData.Phone) ||
          matches(rawData.phone) ||
          matches(store.StoreType) ||
          matches((store as any).salesGroup) ||
          matches((store as any).SalesGroup) ||
          matches(rawData.SalesGroup) ||
          matches(rawData.salesGroup) ||
          matches((store as any).salespersonNo) ||
          matches((store as any).SalespersonNo) ||
          matches(rawData.SalespersonNo) ||
          matches(rawData.salespersonNo) ||
          matches((store as any).salesPersonName) ||
          matches((store as any).SalesPersonName) ||
          matches(rawData.SalesPersonName) ||
          matches(rawData.salesPersonName) ||
          matches((store as any).sortField) ||
          matches(store.SortField) ||
          matches(rawData.SortField) ||
          matches(rawData.sortField) ||
          matches((store as any).state) ||
          matches(store.Province) ||
          matches((store as any).province) ||
          matches(store.Prov) ||
          matches((store as any).Prov) ||
          matches(rawData.Prov) ||
          matches(rawData.Province) ||
          matches(rawData.province) ||
          matches(rawData.state) ||
          matches((store as any).pc) ||
          matches(store.PostalCode) ||
          matches((store as any).PC) ||
          matches(rawData.PostalCode) ||
          matches(rawData.PC) ||
          matches(rawData.pc) ||
          matches((store as any).storeNumber) ||
          matches(store.StoreNumber) ||
          matches(rawData.StoreNumber) ||
          matches(rawData.storeNumber)
        );
      });
    }

    // Apply filters
    if (filters.province) {
      filtered = filtered.filter(store => 
        store.Province?.toUpperCase() === filters.province?.toUpperCase()
      );
    }

    if (filters.city) {
      filtered = filtered.filter(store => 
        store.City?.toLowerCase() === filters.city?.toLowerCase()
      );
    }

    if (filters.salesGroup) {
      filtered = filtered.filter(store => 
        store.StoreType?.toUpperCase() === filters.salesGroup?.toUpperCase()
      );
    }

    setFilteredStores(filtered);
  }, [searchQuery, stores, assignedStores, activeTab, filters]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchQuery(''); // Clear search when switching tabs
    setFilters({}); // Clear filters when switching tabs
  };

  const handleFilterApply = (newFilters: MobileFilterOptions) => {
    setFilters(newFilters);
  };

  const handleRemoveFilter = (key: keyof MobileFilterOptions) => {
    setFilters(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleClearAllFilters = () => {
    setFilters({});
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(v => v !== undefined && v !== '').length;
  };

  const handleMenuToggle = () => {
    setIsMenuCollapsed(!isMenuCollapsed);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleStoreVisit = (storeId: string) => {
    // Navigate to store details with store-visit tab explicitly set
    navigate(`/store-details/${storeId}?tab=store-visit`);
  };

  const handleCall = (phone: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `tel:${phone}`;
  };

  const handleFind = (address: string, city: string, province: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Filter out any undefined values and join with commas
    const locationParts = [address, city, province].filter(part => part && part !== 'undefined');
    const query = encodeURIComponent(locationParts.join(', '));
    window.open(`https://maps.google.com/?q=${query}`, '_blank');
  };

  return (
    <>
      <OffcanvasMenu 
        isCollapsed={isMenuCollapsed}
        onToggle={handleMenuToggle}
        onNavigate={handleNavigation}
      />
      
      <Header 
        isMenuCollapsed={isMenuCollapsed}
        onMenuToggle={handleMenuToggle}
        notificationCount={0}
      />
      
      <PageWrapper>
        <MainContent>
          <BreadcrumbContainer>
            <BreadcrumbItem 
              clickable 
              onClick={() => navigate('/dashboard')}
            >
              <Home size={14} />
              {t('storeVisit.home', 'Home')}
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <Store size={14} />
              {t('storeVisit.title', 'Store Visit')}
            </BreadcrumbItem>
          </BreadcrumbContainer>
          <TitleRow>
            <PageTitle style={{ margin: 0 }}>{t('storeVisit.title')}</PageTitle>
            <RefreshButton
              onClick={handleRefresh}
              disabled={isRefreshing || isLoading}
              spinning={isRefreshing}
            >
              <RefreshCw size={16} />
              {isRefreshing ? t('storeVisit.refreshing', 'Refreshing...') : t('storeVisit.refresh', 'Refresh')}
            </RefreshButton>
          </TitleRow>
          
          <TabContainer>
            <Tab 
              active={activeTab === 'assigned'} 
              onClick={() => handleTabChange('assigned')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill={activeTab === 'assigned' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
              </svg>
              {t('storeVisit.myStores')}
              <TabBadge active={activeTab === 'assigned'}>
                {assignedStores.length}
              </TabBadge>
            </Tab>
            <Tab 
              active={activeTab === 'all'} 
              onClick={() => handleTabChange('all')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              {t('storeVisit.allStores')}
              <TabBadge active={activeTab === 'all'}>
                {stores.length}
              </TabBadge>
            </Tab>
          </TabContainer>
          
          <SearchSection>
            <SearchContainer>
              <SearchIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </SearchIcon>
              <SearchInput 
                placeholder={t('storeVisit.searchPlaceholder')} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </SearchContainer>
            <FilterButton 
              active={getActiveFilterCount() > 0}
              onClick={() => setIsFilterOpen(true)}
            >
              <Filter size={18} />
              {t('storeVisit.filter')}
              {getActiveFilterCount() > 0 && (
                <FilterBadge>{getActiveFilterCount()}</FilterBadge>
              )}
            </FilterButton>
            <MobileFilterChips
              filters={filters}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
              availableProvinces={availableProvinces}
              availableCities={availableCities}
              availableSalesGroups={availableSalesGroups}
            />
          </SearchSection>
          
          {isLoading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>{t('storeVisit.loadingStores')}</LoadingText>
            </LoadingContainer>
          ) : filteredStores.length === 0 ? (
            <EmptyState>
              <EmptyStateIcon>
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
              </EmptyStateIcon>
              <EmptyStateText>{t('storeVisit.noStoresFound')}</EmptyStateText>
              <EmptyStateSubtext>{t('storeVisit.noStoresFoundHint', 'Verify your search details are correct. If the store still doesn\'t appear, tap Refresh to load the latest data.')}</EmptyStateSubtext>
            </EmptyState>
          ) : (
            <StoreList>
              {filteredStores.map(store => (
                <StoreCard key={store.id} onClick={() => handleStoreVisit(store.id)}>
                  <StoreName>{store.StoreName}</StoreName>
                  <CustomerNumberBadge>#{store.CustomerNo}</CustomerNumberBadge>
                  
                  <InfoSection>
                    <StoreAddress>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      {[store.Address, store.City, store.Province, store.PostalCode].filter(part => part && part !== 'undefined').join(', ')}
                    </StoreAddress>
                    
                    {store.Phone && (
                      <StorePhone>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        {store.Phone}
                      </StorePhone>
                    )}
                  </InfoSection>
                  
                  <Divider />
                  
                  <ActionButtons>
                    <ActionButton 
                      variant="tertiary"
                      onClick={(e) => handleCall(store.Phone, e)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                      <ButtonText>{t('storeVisit.call')}</ButtonText>
                    </ActionButton>
                    
                    <ActionButton 
                      variant="secondary"
                      onClick={(e) => handleFind(store.Address, store.City, store.Province, e)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                      <ButtonText>{t('storeVisit.find')}</ButtonText>
                    </ActionButton>
                    
                    <ActionButton 
                      variant="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStoreVisit(store.id);
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                        <polyline points="10 17 15 12 10 7"></polyline>
                        <line x1="15" y1="12" x2="3" y2="12"></line>
                      </svg>
                      <ButtonText>{t('storeVisit.visit')}</ButtonText>
                    </ActionButton>
                  </ActionButtons>
                </StoreCard>
              ))}
            </StoreList>
          )}
        </MainContent>
      </PageWrapper>

      <MobileFilterSheet
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={handleFilterApply}
        availableProvinces={availableProvinces}
        availableCities={availableCities}
        availableSalesGroups={availableSalesGroups}
        showSalesGroupFilter={true}
        currentFilters={filters}
      />
    </>
  );
};